import {
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  useRef,
  useState,
} from "react";
import { Stack } from "@volca/ui/components/ui/stack";

const STEP = 2;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/**
 * Two panes side by side, the line between them dragged (or moved with the
 * arrow keys) to give one more room; stacked on a narrow screen. Sizes are
 * the left pane's share of the width, in percent.
 */
export const SplitPanes = ({
  left,
  right,
  label,
  defaultSize = 62,
  min = 35,
  max = 75,
}: {
  left: ReactNode;
  right: ReactNode;
  /** Names the divider: "Resize the file and the figures". */
  label: string;
  defaultSize?: number;
  min?: number;
  max?: number;
}) => {
  const [size, setSize] = useState(defaultSize);
  const container = useRef<HTMLDivElement>(null);
  const resize = (event: PointerEvent<HTMLDivElement>) => {
    const box = container.current?.getBoundingClientRect();
    if (!box?.width) return;
    setSize(clamp(((event.clientX - box.left) / box.width) * 100, min, max));
  };
  const keys = (event: KeyboardEvent<HTMLDivElement>) => {
    const next = {
      ArrowLeft: size - STEP,
      ArrowRight: size + STEP,
      Home: min,
      End: max,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    setSize(clamp(next, min, max));
  };
  return (
    <Stack
      ref={container}
      gap={0}
      align="stretch"
      className="min-h-0 flex-1 lg:flex-row"
      style={{ "--split": `${size}%` } as CSSProperties}
    >
      <Stack
        gap={0}
        align="stretch"
        className="min-h-96 min-w-0 flex-1 lg:min-h-0 lg:flex-none lg:basis-(--split)"
      >
        {left}
      </Stack>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={label}
        aria-valuenow={Math.round(size)}
        aria-valuemin={min}
        aria-valuemax={max}
        tabIndex={0}
        onKeyDown={keys}
        onPointerDown={(event) =>
          event.currentTarget.setPointerCapture(event.pointerId)
        }
        onPointerMove={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId))
            resize(event);
        }}
        onPointerUp={(event) =>
          event.currentTarget.releasePointerCapture(event.pointerId)
        }
        className="group relative z-10 hidden w-px flex-none cursor-col-resize touch-none bg-line outline-none lg:block"
      >
        {/* A wider grip than the line it draws. */}
        <span className="absolute inset-y-0 -left-1.5 w-3" />
        <span className="absolute inset-y-0 -left-px w-0.75 bg-transparent transition-colors group-hover:bg-line-strong group-focus-visible:bg-focus group-active:bg-focus" />
      </div>
      <Stack
        gap={0}
        align="stretch"
        className="min-h-0 min-w-0 flex-1 border-t border-line lg:border-t-0"
      >
        {right}
      </Stack>
    </Stack>
  );
};
